import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type PracticeSettings } from '../api/settingsApi';
import { settingsKeys } from '../queryKeys';
import { Card } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { authKeys } from '@/modules/auth/queryKeys';
import { agencyKeys } from '@/modules/agency/queryKeys';
import { PageHeader } from '@/shared/ui';
import { getVerticalLabel, specialtyOptions } from '@/shared/constants/verticals';
import { LocationsSection } from './LocationsPage';
import { locationsApi, type LocationData } from '../api/locationsApi';
import styles from './SettingsPage.module.css';

// The phone is the primary location's (backend keeps the two one number), so it is edited on the location.
type FormState = Pick<PracticeSettings, 'name' | 'specialty' | 'website'>;

const EMPTY: FormState = { name: '', specialty: '', website: '' };

/** The practice's own details: name, specialty and main website. */
function PracticeDetailsCard({
  locationCount,
  onlyLocation,
}: {
  locationCount: number | null;
  /** The practice's only location: its website is the practice's, so the one field here saves both. */
  onlyLocation: LocationData | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { impersonatedPractice, renameImpersonatedPractice } = useImpersonation();

  const { data, isLoading } = useQuery({
    queryKey: settingsKeys.practice(),
    queryFn: () => settingsApi.getPractice(),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Agency-set and read-only here; it scopes which specialties are selectable.
  const vertical = data?.data?.vertical;

  // What is saved now; the form starts from it and Save is enabled only once they differ.
  const saved: FormState = data?.data
    ? {
        name: data.data.name ?? '',
        specialty: data.data.specialty ?? '',
        website: onlyLocation?.website || data.data.website || '',
      }
    : EMPTY;
  const dirty = (Object.keys(EMPTY) as (keyof FormState)[]).some(
    (k) => (form[k] ?? '') !== (saved[k] ?? ''),
  );

  useEffect(() => {
    if (!data?.data) return;
    setForm(saved);
    // `saved` is derived from exactly these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, onlyLocation]);

  const set = (field: keyof FormState) => (e: { target: { value: string } }) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = 'Practice name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateMutation = useMutation({
    mutationFn: async (updates: FormState) => {
      await settingsApi.updatePractice(updates);
      if (!onlyLocation) return;
      // One location reads as the practice itself: its website is the practice's, and while its name is still the
      // practice's name, renaming the practice renames it too. A location given its own name keeps it.
      const locationUpdates: { website?: string; name?: string } = {};
      if ((onlyLocation.website ?? '') !== (updates.website ?? ''))
        locationUpdates.website = updates.website ?? '';
      if (onlyLocation.name === saved.name && updates.name.trim() !== saved.name)
        locationUpdates.name = updates.name.trim();
      if (Object.keys(locationUpdates).length > 0)
        await locationsApi.update(onlyLocation.id, locationUpdates);
    },
    onSuccess: (_result, updates) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.practice() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.locations() });
      // The top bar's practice name comes from the switcher's practice lists (and, for an agency
      // user, the name stored when the practice was opened) — refresh those too.
      queryClient.invalidateQueries({ queryKey: authKeys.practices() });
      queryClient.invalidateQueries({ queryKey: agencyKeys.allPractices() });
      if (impersonatedPractice) {
        renameImpersonatedPractice(impersonatedPractice.id, updates.name.trim());
        // Carries the location names the location switcher shows.
        if (onlyLocation)
          queryClient.invalidateQueries({ queryKey: agencyKeys.practice(impersonatedPractice.id) });
      }
      toast.success('Practice settings saved.');
    },
    onError: () => toast.error('Failed to save settings.'),
  });

  return (
    <Card title={locationCount !== null && locationCount > 1 ? 'Practice' : 'General information'}>
      <div className={styles.fieldForm}>
        <Input
          label="Practice Name"
          value={form.name}
          onChange={set('name')}
          error={errors.name}
          fullWidth
          disabled={isLoading}
        />
        {/*
          Scoped to the practice's vertical. Using the dental list here meant a
          medical practice's saved specialty had no matching option, so the
          select fell back to the placeholder and looked unset — and touching it
          would have replaced e.g. Cardiology with a dental specialty.
        */}
        <Select
          label="Specialty"
          value={form.specialty ?? ''}
          onChange={set('specialty')}
          options={specialtyOptions(vertical)}
          hint={`${getVerticalLabel(vertical)} specialties. The vertical itself is set by your agency.`}
          fullWidth
          disabled={isLoading}
        />
        <Input
          label={locationCount !== null && locationCount > 1 ? 'Main website' : 'Website'}
          value={form.website ?? ''}
          onChange={set('website')}
          placeholder="https://example.com"
          fullWidth
          disabled={isLoading}
        />
      </div>
      <div className={styles.actionsFooter}>
        <Button
          variant="primary"
          loading={updateMutation.isPending}
          disabled={isLoading || !dirty}
          onClick={() => {
            if (validate()) updateMutation.mutate({ ...form, name: form.name.trim() });
          }}
        >
          {dirty || updateMutation.isPending ? 'Save Changes' : 'Saved'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Settings > Practice & Locations. With one location it reads as one practice: its name, specialty and website, then
 * the location's address, phone and time zone, each typed once. With more, the practice card keeps what they share
 * and each location has its own card.
 */
export function PracticeSettingsPage() {
  const [locations, setLocations] = useState<LocationData[] | null>(null);
  return (
    <div className={styles.page}>
      <PageHeader
        title="Practice & Locations"
        subtitle="Your practice and every office it runs. Each location tracks its own campaigns, reviews and visibility."
      />
      <PracticeDetailsCard
        locationCount={locations?.length ?? null}
        onlyLocation={locations?.length === 1 ? (locations[0] ?? null) : null}
      />
      <LocationsSection onLocations={setLocations} />
    </div>
  );
}
